import { useState, useEffect } from "react";
import { ResponsivePie } from "@nivo/pie";
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { useActiveTheme } from "../../../context/ThemeContext";
import type { WidgetComponentProps, CustomChartConfig } from "../types";

export default function CustomChartWidget({ config, title }: WidgetComponentProps) {
  const COLORS = useActiveTheme().colors.chart;
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);

  const chartConfig = (config.chartConfig as CustomChartConfig | undefined)
    ?? (config as unknown as CustomChartConfig | undefined);

  useEffect(() => {
    if (!chartConfig) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const res = await fetch("/api/custom-chart", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(chartConfig),
        });
        const result = await res.json();
        if (result.success) {
          setData(result.data || []);
        }
      } catch (err) {
        console.error("Failed to fetch custom chart data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [chartConfig]);

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 h-full flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[var(--brand-primary)]/20 border-t-[var(--brand-primary)] rounded-full animate-spin" />
      </div>
    );
  }

  if (!chartConfig || data.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 h-full flex flex-col items-center justify-center text-muted-foreground">
        <p className="text-sm">No data available</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-6 h-full flex flex-col overflow-hidden">
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      <div className="flex-1 min-h-0">
        {chartConfig.chartType === "pie" ? (
          <ResponsivePie
            data={data.map((d, i) => ({
              id: String(d.label || d.name || d._id || i),
              label: String(d.label || d.name || d._id || i),
              value: Number(d.value || 0),
              color: COLORS[i % COLORS.length],
            }))}
            margin={{ top: 30, right: 80, bottom: 30, left: 80 }}
            innerRadius={0.5}
            padAngle={1}
            cornerRadius={3}
            colors={{ datum: "data.color" }}
            borderWidth={2}
            borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
            enableArcLinkLabels={true}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
            enableArcLabels={true}
            arcLabelsSkipAngle={10}
            arcLabelsTextColor="white"
          />
        ) : chartConfig.chartType === "bar" ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip />
              <Bar dataKey="value" fill={COLORS[0]} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : chartConfig.chartType === "line" ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke={COLORS[0]} strokeWidth={3} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip />
              <Area type="monotone" dataKey="value" stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.2} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
